import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { Catalog } from '../../api/CataloguesAPI';
import { catalogHeroMedia } from '../../shared/catalogLookbook';
import { PublicCatalogCtas } from './PublicCatalogCtas';
import PublicCatalogMedia, { CATALOG_MEDIA_SIZES } from './PublicCatalogMedia';

export const HERO_DESCRIPTION_COLLAPSE_CHARS = 180;

type PublicCatalogHeroProps = {
  catalog: Catalog;
  onImport: () => void;
};

const PublicCatalogHero = ({ catalog, onImport }: PublicCatalogHeroProps) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const media = catalogHeroMedia(catalog.items || []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const expandedDescriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prefersReducedMotion || media.length < 2) {
      return;
    }
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % media.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [media.length, prefersReducedMotion]);

  useEffect(() => {
    if (!descriptionExpanded) {
      return;
    }
    expandedDescriptionRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [descriptionExpanded, prefersReducedMotion]);

  const itemCount = catalog.items?.length || 0;
  const description = catalog.description?.trim() || '';
  const canToggleDescription = description.length > HERO_DESCRIPTION_COLLAPSE_CHARS;

  return (
    <>
    <section className="relative h-[70vh] min-h-[420px] max-h-[720px] overflow-hidden bg-gray-900">
      <style>{`
        @keyframes catalogKenBurns {
          0% { transform: scale(1) translate3d(0, 0, 0); }
          100% { transform: scale(1.08) translate3d(-1.5%, -1%, 0); }
        }
        .catalog-ken-burns {
          animation: catalogKenBurns 18s ease-in-out alternate infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .catalog-ken-burns {
            animation: none;
          }
        }
      `}</style>
      {media.length > 0 ? (
        media.map((url, index) => (
          <div
            key={url}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === activeIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <PublicCatalogMedia
              src={url}
              alt=""
              className="catalog-ken-burns h-full w-full object-cover"
              sizes={CATALOG_MEDIA_SIZES.hero}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'low'}
            />
          </div>
        ))
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-as-pink-200 via-as-pink-100 to-as-pink-400" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/20" />
      <div className="relative z-10 flex h-full flex-col items-center justify-end px-4 pb-16 text-center text-white sm:pb-20">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-white/80">
          {catalog.storeName}
        </p>
        <h1
          className="max-w-4xl font-light tracking-[0.18em] text-4xl uppercase sm:text-5xl md:text-6xl"
          style={{ fontFamily: 'trajan-pro-regular, Inter, serif' }}
        >
          {catalog.name}
        </h1>
        {description && (
          <div className="mt-4 max-w-2xl">
            <p
              className={`text-base font-light leading-relaxed text-white/90 sm:text-lg ${
                canToggleDescription ? 'line-clamp-3' : ''
              }`}
            >
              {description}
            </p>
            {canToggleDescription && !descriptionExpanded && (
              <button
                type="button"
                aria-expanded={false}
                onClick={() => setDescriptionExpanded(true)}
                className="mt-2 text-sm font-medium tracking-wide text-white/90 underline decoration-white/40 underline-offset-4 hover:text-white"
              >
                {t('marketplace.catalogues.lookbook.read_more')}
              </button>
            )}
          </div>
        )}
        <p className="mt-3 text-sm tracking-[0.16em] uppercase text-white/70">
          {t('marketplace.catalogues.lookbook.item_count', { count: itemCount })}
        </p>
        <div className="mt-8 hidden md:block">
          <PublicCatalogCtas catalog={catalog} onImport={onImport} />
        </div>
      </div>
    </section>
    {descriptionExpanded && (
      <div
        ref={expandedDescriptionRef}
        className="border-b border-border bg-background px-4 py-10 sm:px-6"
      >
        <div className="mx-auto max-w-2xl text-center">
          <p className="whitespace-pre-wrap text-base font-light leading-relaxed text-foreground sm:text-lg">
            {description}
          </p>
          <button
            type="button"
            aria-expanded={true}
            onClick={() => setDescriptionExpanded(false)}
            className="mt-4 text-sm font-medium tracking-wide text-as-pink-500 underline decoration-as-pink-200 underline-offset-4 hover:text-as-pink-300"
          >
            {t('marketplace.catalogues.lookbook.show_less')}
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default PublicCatalogHero;
